/**
 * Site header: the mega menu (one sheet whose panel and height follow the open item), the sticky header's glass
 * background, and --site-header-height for anchors and sticky elements below it.
 */
export default (el) => {
  const root = document.documentElement,
        nav = el.querySelector('.site-header-nav'),
        sheet = el.querySelector('.mega-menu'),
        triggers = [...el.querySelectorAll('.site-header-nav-trigger')],
        desktop = window.matchMedia('(min-width: 1200px)'),
        fineHover = window.matchMedia('(hover: hover) and (pointer: fine)')
  ;

  // Header height, for scroll-padding and the sticky elements that sit below the header
  const setHeight = () => root.style.setProperty('--site-header-height', `${el.offsetHeight}px`);
  setHeight();
  if (window.ResizeObserver) {
    new ResizeObserver(setHeight).observe(el);
  }

  // Glass background once the header reaches its sticky position: the top of the window, or under the admin bar
  // when logged in
  const setStuck = () => el.classList.toggle(
    '-stuck',
    el.getBoundingClientRect().top <= parseFloat(getComputedStyle(el).top) + 1 && window.scrollY > 4
  );

  if (!sheet) {
    window.addEventListener('scroll', setStuck, { passive: true });
    setStuck();
    return;
  }

  const panelFor = (trigger) => document.getElementById(trigger.getAttribute('aria-controls')),
        panels = triggers.map(panelFor);

  // The open trigger, how it opened ('hover', 'click' or 'key') and the scroll position when it did
  let current = null,
      openedBy = null,
      openedAt = 0,
      openTimer = 0,
      closeTimer = 0;

  // Links and buttons in a panel, in order
  const focusables = (panel) => [...panel.querySelectorAll('a, button')];

  const setInstant = (on) => {
    sheet.classList.toggle('-instant', on);
    if (on) {
      void sheet.offsetWidth;
    }
  };

  const layout = () => {
    if (current) {
      sheet.style.setProperty('--mega-menu-height', `${panelFor(current).offsetHeight}px`);
    }
  };

  const open = (trigger, by) => {
    clearTimeout(closeTimer);
    if (current === trigger) {
      return;
    }
    const first = !current;
    current = trigger;
    openedBy = by;

    triggers.forEach((t) => t.setAttribute('aria-expanded', String(t === trigger)));
    if (first) {
      setInstant(true);
    }
    panels.forEach((panel) => {
      const active = panel === panelFor(trigger);
      panel.classList.toggle('-active', active);
      panel.inert = !active;
    });
    layout();
    if (first) {
      void sheet.offsetWidth;
      setInstant(false);
    }

    sheet.inert = false;
    sheet.dataset.open = 'true';
    el.classList.add('-open');
    openedAt = window.scrollY;
  };

  const close = (returnFocus = false) => {
    clearTimeout(openTimer);
    if (!current) {
      return;
    }
    const trigger = current;
    current = null;
    triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    sheet.dataset.open = 'false';
    sheet.inert = true;
    el.classList.remove('-open');
    if (returnFocus) {
      trigger.focus();
    }
  };

  triggers.forEach((trigger, i) => {
    trigger.addEventListener('mouseenter', () => {
      if (!fineHover.matches) {
        return;
      }
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      openTimer = setTimeout(() => open(trigger, 'hover'), current ? 0 : 80);
    });

    trigger.addEventListener('mouseleave', () => clearTimeout(openTimer));

    // A click keeps a hover-opened menu open; a second click closes it
    trigger.addEventListener('click', () => {
      if (current === trigger && openedBy !== 'hover') {
        close();
      } else {
        open(trigger, 'click');
      }
      openedBy = 'click';
    });

    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const next = triggers[(i + (e.key === 'ArrowRight' ? 1 : triggers.length - 1)) % triggers.length];
        next.focus();
        if (current) {
          open(next, 'key');
        }
      } else if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey && current === trigger)) {
        if (e.key === 'ArrowDown' && current !== trigger) {
          open(trigger, 'key');
        }
        const first = focusables(panelFor(trigger))[0];
        if (first) {
          e.preventDefault();
          requestAnimationFrame(() => first.focus());
        }
      }
    });
  });

  // Tab past the end of a panel goes to the next item in the bar; Shift+Tab before its start goes back to its trigger
  sheet.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      close(true);
      return;
    }
    if (e.key !== 'Tab' || !current) {
      return;
    }
    const items = focusables(panelFor(current)),
          index = items.indexOf(document.activeElement),
          bar = [...nav.querySelectorAll(':scope > ul > li > :is(a, button)')],
          next = bar[bar.indexOf(current) + 1];

    if (!e.shiftKey && index === items.length - 1) {
      e.preventDefault();
      const trigger = current;
      close();
      (next || el.querySelector('.site-header-search-toggle') || trigger).focus();
    } else if (e.shiftKey && index === 0) {
      e.preventDefault();
      current.focus();
    }
  });

  // Pointing at a plain link in the bar closes a menu that hover opened
  nav.querySelectorAll('.site-header-nav-link').forEach((link) => {
    link.addEventListener('mouseenter', () => {
      clearTimeout(openTimer);
      if (current && openedBy === 'hover') {
        close();
      }
    });
  });

  nav.addEventListener('mouseenter', () => clearTimeout(closeTimer));
  nav.addEventListener('mouseleave', () => {
    if (fineHover.matches && openedBy === 'hover') {
      closeTimer = setTimeout(() => close(), 220);
    }
  });

  document.addEventListener('click', (e) => {
    if (current && !nav.contains(e.target)) {
      close();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && current) {
      close(nav.contains(document.activeElement));
    }
  });

  window.addEventListener('resize', () => {
    if (!current) {
      return;
    }
    if (!desktop.matches) {
      close();
      return;
    }
    setInstant(true);
    layout();
    setInstant(false);
  });

  // An open menu closes once the page scrolls away under it
  window.addEventListener('scroll', () => {
    setStuck();
    if (current && Math.abs(window.scrollY - openedAt) > 80 && !nav.contains(document.activeElement)) {
      close();
    }
  }, { passive: true });
  setStuck();
};
